import { IDataPointsAddFields, IAdditionalFields, IDataPoints } from '@/models/AdditionalFields';
import IFieldRuleData from '@/models/FieldRuleData';
import IFieldSettingsData from '@/models/FieldSettingsData';
import IFormFieldRules from '@/models/form-field/IFormFieldRules';
import WebEnvConfig from '@/models/WebEnvConfig';
import ServiceHelperFactory from './ServiceHelperFactory';
import BaseApiService from '../BaseApiService';
import IServiceHelper from './IServiceHelper';
import IFormFields from '@/models/forms/IFormFields';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import IFormFieldsAdditionalDetails from '@/models/forms/IFormFieldsAdditionalDetails';
import IFieldSubjectCompRelationship from '@/models/forms/IFieldSubjectCompRelationship';

export default class FormsService extends BaseApiService {
    constructor(webEnvConfig: WebEnvConfig) {
        super(webEnvConfig.urls?.quickSourceApi ?? '');
    }

    getServiceHelper(): IServiceHelper {
        return ServiceHelperFactory.getHelper();
    }

    async getDataFields(formId: string): Promise<IFormFields | null> {
        this.addAcceptJsonHeader();

        try {
            const response = await this.axiosInstance.get(`v7/forms/${formId}/fields`);
            if (response.status === 200) {
                const result: IFormFields = response.data;
                return result;
            }
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            console.log(`FormsService.getDataFields(), Error[${errorDescription}] on formId[${formId}]`);
        }
        return null;
    }

    async getDataFieldsAdditionalDetails(formId: string): Promise<IFormFieldsAdditionalDetails | null> {
        this.addAcceptJsonHeader();

        const response = await this.axiosInstance.get(`v7/forms/${formId}/fields-additional-details`);
        if (response.status === 200) {
            const result: IFormFieldsAdditionalDetails = response.data;
            return result;
        }

        return null;
    }

    async getSubjectCompRelationship(formId: string, isSubject: boolean): Promise<IFieldSubjectCompRelationship | null> {
        this.addAcceptJsonHeader();

        let queryParams = {
            isSubject: isSubject,
        }

        const query = this.buildQuery(queryParams);

        const response = await this.axiosInstance.get(`v7/fieldmappings/${formId}/grouped${query}`);
        if (response.status === 200) {
            const result: IFieldSubjectCompRelationship = response.data;
            return result;
        }

        return null;
    }


    // async getAdditionalFields(formId: string): Promise<IDataPointsAddFields> {
    //     let addFields: IAdditionalFields[] = [];
    //     let dataPoints: IDataPoints[] = [];
    //     this.addAcceptJsonHeader();
    //     const response = await this.axiosInstance.get(`v5/forms/${formId}/additional-fields`);

    //     dataPoints = response.data.dataPoints;
    //     addFields = response.data.additionalFields;

    //     const result: IDataPointsAddFields = {
    //         dataPoints: dataPoints,
    //         additionalFields: addFields,
    //     };

    //     return result;
    // }

    async getDataFieldRules(fieldId: number): Promise<IFieldRuleData[]> {
        let result: IFieldRuleData[] = [];
        this.addAcceptJsonHeader();

        const response = await this.axiosInstance.get(`v5/fields/${fieldId}/rules`);
        if (response.data) {
            result = response.data;
        }
        return result;
    }

    async getDataFieldRulesPreviewer(params: any) {
        let result = '';
        this.addContentTypeJsonHeader();

        const data = JSON.stringify({
            fieldId: params.fieldId,
            casing: params.preferredCasing,
            rules: params.fieldRules,
            value: params.value,
            formId: theLocalStorageUtils.get(LocalStorageKeys.formCode)?.toString(),
        });

        const response = await this.axiosInstance.post(`v6/fields/${params.fieldId}/rules/preview`, data);
        if (response.data) {
            result = response.data;
        }
        return result;
    }

    async getDataFieldSettings(fieldId: number): Promise<IFieldSettingsData[]> {
        let result: IFieldSettingsData[] = [];
        this.addAcceptJsonHeader();

        const response = await this.axiosInstance.get(`v5/fields/${fieldId}/settings`);
        if (response.data) {
            result = response.data;
        }
        return result;
    }

    async saveDataPreferenceSourceOrder(payload: any) {
        this.addContentTypeJsonHeader();

        const data = JSON.stringify({
            dataPriorityOrder: payload.dataPreferenceSourceOrder,
        });
        return await this.axiosInstance.patch(`v5/fields/${payload.fieldId}/settings`, data);
    }

    async savePreferredCasing(payload: any) {
        this.addContentTypeJsonHeader();
        const data = JSON.stringify({
            preferredCasing: payload.preferredCasing,
        });
        return await this.axiosInstance.patch(`v5/fields/${payload.fieldId}/settings`, data);
    }

    async saveFieldRules(payload: any) {
        this.addContentTypeJsonHeader();
        return await this.axiosInstance.put(`v5/fields/${payload.fieldId}/rules`, payload.fieldRules);
    }

    async runFieldRules(payload: IFormFieldRules): Promise<IFormFieldRules | null> {
        this.addContentTypeJsonHeader();

        try {
            const data = JSON.stringify(payload);
            const formId = theLocalStorageUtils.get(LocalStorageKeys.formCode);
            const response = await this.axiosInstance.post(`v6/fields/rules/${formId}/run`, data);
            if (response.status === 200) {
                const result: IFormFieldRules = response.data;
                return result;
            }
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            console.log(`FormsService.runFieldRules(), Error[${errorDescription}]`);
        }

        return null;
    }
    async putAdditionalFields(payload: any) {
        this.addContentTypeJsonHeader();
        const data = JSON.stringify(payload.data);
        return await this.axiosInstance.put(`v5/forms/${payload.formId}/additional-fields`, data);
    }

    async saveSuggestFormattingRule(payload: any) {
        this.addContentTypeJsonHeader();
        const data = JSON.stringify({
            suggestFormattingRule: payload.suggestFormattingRule,
        });
        return await this.axiosInstance.patch(`v5/fields/${payload.fieldId}/settings`, data);
    }
}
